import { Check, Copy } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useUserAuth } from '@/lib/auth/UserAuthContext';

/**
 * One-shot modal that surfaces the freshly minted recovery code.
 *
 * The plan calls for showing this once at first sign-in (and again
 * whenever a recovery-code consume mints a replacement). We never
 * persist the code; once dismissed, the user has to email-sign-in to
 * regenerate one.
 */
export function RecoveryCodeDialog(): React.JSX.Element | null {
  const { recoveryCode, clearRecoveryCode } = useUserAuth();
  const [copied, setCopied] = React.useState(false);

  const handleCopy = React.useCallback(() => {
    if (!recoveryCode) return;
    void navigator.clipboard
      .writeText(recoveryCode)
      .then(() => {
        setCopied(true);
        toast.success('Recovery code copied');
        setTimeout(() => {
          setCopied(false);
        }, 2_000);
      })
      .catch(() => {
        toast.error('Could not copy — write it down by hand');
      });
  }, [recoveryCode]);

  if (!recoveryCode) return null;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) clearRecoveryCode();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save your recovery code</DialogTitle>
          <DialogDescription>
            We'll only show this once. If you ever lose access to your email, paste this code on the
            sign-in page to get back in.
          </DialogDescription>
        </DialogHeader>
        <pre
          data-testid="recovery-code-text"
          className="select-all whitespace-pre-wrap break-all rounded-md border border-border bg-surface px-3 py-2 font-mono text-body"
        >
          {recoveryCode}
        </pre>
        <DialogFooter>
          <Button variant="outline" onClick={handleCopy}>
            {copied ? (
              <>
                <Check className="mr-2 h-4 w-4" />
                Copied
              </>
            ) : (
              <>
                <Copy className="mr-2 h-4 w-4" />
                Copy
              </>
            )}
          </Button>
          <Button onClick={clearRecoveryCode}>I saved it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
